import { createContext } from 'react'

import type { ZoneSummary } from '@/api/types'

/**
 * The active zone is console-wide state, not a route parameter.
 *
 * §16 moved the isolation boundary to the zone, so almost every screen is
 * zone-scoped. Rather than repeat `/app/zones/:zoneId/...` on every route, the
 * shell owns one selection, persists it, and each page reads it from here.
 */
export interface ZoneContextValue {
  /**
   * Every zone this user may see, in every lifecycle state — archived ones
   * included, because §17 keeps their history readable forever and the console
   * is the only way to read it. Writes are gated on `zone.reachable`, not on
   * membership of this list.
   */
  zones: ZoneSummary[]
  activeZone: ZoneSummary | undefined
  /** Convenience: `activeZone?.id`. Zone-scoped queries key off this. */
  activeZoneId: string | undefined
  selectZone: (zoneId: string) => void
  isLoading: boolean
  isError: boolean
  error: unknown
  refetch: () => void
  /**
   * The persisted choice pointed at a zone this user can no longer reach (it
   * was archived, or their grant was revoked) and the console silently moved
   * them. True until dismissed — the user is told, not left guessing.
   */
  lostPersistedZone: boolean
  dismissLostZone: () => void
}

export const ZoneContext = createContext<ZoneContextValue | undefined>(undefined)
